/**
 * Reference Image Cache — persists found images per world in localStorage.
 * Deduplicates by URL, caps at 50 entries, auto-expires after 7 days.
 *
 * P-17 fix: maintains a global index key (`ref_image_cache_index`) listing all
 * worldIds that have ever been cached.  On each write the index is updated, and
 * a lightweight pruning pass removes index entries whose localStorage key has
 * been entirely cleared or whose every entry has expired.  This prevents
 * unbounded growth when a user visits many worlds over time.
 */

export interface CachedImage {
  url: string;
  thumbnail?: string;
  source: "web" | "codex";
  keyword: string;
  foundAt: number;
}

const MAX_ENTRIES = 50;
const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const INDEX_KEY = "ref_image_cache_index";

function cacheKey(worldId: string) {
  return `ref_image_cache_${worldId}`;
}

/** Read (and lazily parse) the global world-id index. */
function readIndex(): string[] {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

/** Persist an updated index. */
function writeIndex(ids: string[]) {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(ids));
  } catch {
    // Storage full — not critical; skip silently.
  }
}

/**
 * Remove index entries whose cache key is missing or fully expired.
 * Called on every write so the index stays tidy without a separate sweep.
 * Very cheap: reads only the index string + does a single JSON.parse per world
 * that might still have data (early-exits on first valid entry).
 */
function pruneIndex() {
  const now = Date.now();
  const ids = readIndex();
  const alive = ids.filter((worldId) => {
    try {
      const raw = localStorage.getItem(cacheKey(worldId));
      if (!raw) return false;
      const items: CachedImage[] = JSON.parse(raw);
      return items.some((i) => now - i.foundAt < EXPIRY_MS);
    } catch {
      return false;
    }
  });
  if (alive.length !== ids.length) writeIndex(alive);
}

/** Add worldId to the global index if not already present. */
function registerInIndex(worldId: string) {
  const ids = readIndex();
  if (!ids.includes(worldId)) {
    ids.push(worldId);
    writeIndex(ids);
    // Prune stale entries opportunistically — keeps index compact without a
    // dedicated scheduled job.  We prune AFTER adding so the fresh worldId is
    // always included even if its key doesn't exist yet.
    pruneIndex();
  }
}

/** Read cache, filtering out expired entries. */
export function getReferenceImageCache(worldId: string): CachedImage[] {
  try {
    const raw = localStorage.getItem(cacheKey(worldId));
    if (!raw) return [];
    const items: CachedImage[] = JSON.parse(raw);
    const now = Date.now();
    const valid = items.filter((i) => now - i.foundAt < EXPIRY_MS);
    // Persist pruned list if we removed anything
    if (valid.length !== items.length) {
      localStorage.setItem(cacheKey(worldId), JSON.stringify(valid));
    }
    return valid;
  } catch {
    return [];
  }
}

/** Add images to cache, deduplicating by URL, capping at MAX_ENTRIES. */
export function addToReferenceImageCache(worldId: string, images: CachedImage[]) {
  if (!images.length) return;
  const existing = getReferenceImageCache(worldId);
  const urlSet = new Set(existing.map((i) => i.url));
  const newItems = images.filter((i) => !urlSet.has(i.url));
  const merged = [...existing, ...newItems].slice(-MAX_ENTRIES);
  try {
    localStorage.setItem(cacheKey(worldId), JSON.stringify(merged));
  } catch {
    // Storage full — clear and retry with just new items
    localStorage.removeItem(cacheKey(worldId));
    localStorage.setItem(cacheKey(worldId), JSON.stringify(merged.slice(-20)));
  }
  // P-17: track this worldId in the global index so the GC can find it later.
  registerInIndex(worldId);
}

/** Clear all cached images for a world. */
export function clearReferenceImageCache(worldId: string) {
  localStorage.removeItem(cacheKey(worldId));
}
